import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { Stats } from "@msuite/ui/stats";
import { DataTable } from "@msuite/ui/table";
import { toast } from "@msuite/ui/toast";
import { downloadFile, responseHandler } from "@msuite/ui/utils";
import {
	type IncomingInvoicePaymentRunFormSchema,
	incomingInvoicePaymentRunFormSchema,
} from "@msuite/utils/schemas";
import type { IncomingInvoiceListItem } from "@msuite/utils/types";
import { useMemo } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { formatInvoiceAmount } from "@/components/incoming-invoices";
import { IncomingInvoicePaymentRunForm } from "@/forms/incoming-invoice-payment-run-form";
import { client } from "@/hono";
import { useWorkspaces } from "@/hooks/use-workspaces";
import { columns } from "./table-config";
import { toPaymentPreviews } from "./to-payment-previews";

/** Props Interface */
interface ExportPaymentsModalProps extends ModalProps {
	incomingInvoices: IncomingInvoiceListItem[];
	onComplete?: () => void;
}

/**
 * Export a payment file over the invoices picked. The preview applies the server's own rules, so
 * an invoice that cannot be paid is named before the export is asked for, and the export is
 * refused as a whole while one is. The outgoing invoices planned against a payment on its invoice's
 * detail page are taken off its transfer; a payment they cover in full has no transfer line, and a
 * file of only such payments has no document to download.
 */
export function ExportPaymentsModal({
	isOpen,
	onClose,
	incomingInvoices,
	onComplete,
}: ExportPaymentsModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<IncomingInvoicePaymentRunFormSchema>({
		defaultValues: { isSkontoApplied: true, format: "pain.001.001.09" },
		resolver: resolver(incomingInvoicePaymentRunFormSchema),
	});
	const isSkontoApplied = useWatch({ control: methods.control, name: "isSkontoApplied" });

	/** Query */
	const { workspaces } = useWorkspaces();

	/** Functions */
	async function handleOnSubmit(json: IncomingInvoicePaymentRunFormSchema) {
		const response = await client.incoming_invoices["payment-runs"].$post({
			json: { ...json, incomingInvoiceIds: incomingInvoices.map((invoice) => invoice.id) },
		});
		const { paymentRun } = await responseHandler(response);
		const count = `${incomingInvoices.length}`;
		if (paymentRun.document) {
			downloadFile(
				new Blob([paymentRun.document], { type: "application/xml" }),
				paymentRun.fileName,
			);
			toast.success(t("incoming_invoices.payments.exported", { count }));
		} else {
			toast.success(t("incoming_invoices.payments.offsets.settled_without_file", { count }));
		}
		await qc.invalidateQueries({ predicate: invalidate(["incoming-invoices"]) });
		// The planned offsets the file booked settle outgoing invoices.
		if (previews.some((preview) => preview.offsetNumbers.length)) {
			await qc.invalidateQueries({ predicate: invalidate(["invoices"]) });
		}
		onComplete?.();
		onClose();
	}

	/** Computed */
	const previews = useMemo(
		() => toPaymentPreviews(incomingInvoices, workspaces, isSkontoApplied),
		[incomingInvoices, workspaces, isSkontoApplied],
	);
	let totalCents = 0;
	let skontoCents = 0;
	for (const preview of previews) {
		totalCents += Math.round((preview.transferAmount ?? 0) * 100);
		skontoCents += Math.round((preview.skontoAmount ?? 0) * 100);
	}
	const hasUnpayable = previews.some((preview) => preview.reason !== null);

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("incoming_invoices.payments.export_payment_file")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
				width="80vw"
			>
				<Modal.Body>
					<Modal.TableWrapper>
						<DataTable
							data={previews}
							columns={columns}
							isLoading={false}
							refetch={undefined}
							queryKey={undefined}
							menu={null}
							enableSearch={false}
							persistenceKey="incoming-invoice-payment-preview-table"
							slots={{
								"below-search": () => (
									<Stats.Container className="card divide-x-[0.5px] *:border-color p-0 *:py-2 *:px-4 gap-0">
										<Stats>
											<Stats.Label>{t("incoming_invoices.payments.invoice_count")}</Stats.Label>
											<Stats.Value>{previews.length}</Stats.Value>
										</Stats>
										<Stats>
											<Stats.Label>{t("incoming_invoices.payments.total")}</Stats.Label>
											<Stats.Value forceNumber={false}>
												{formatInvoiceAmount(totalCents / 100, "EUR")}
											</Stats.Value>
										</Stats>
										<Stats>
											<Stats.Label>{t("incoming_invoices.payments.skonto_total")}</Stats.Label>
											<Stats.Value forceNumber={false}>
												{formatInvoiceAmount(skontoCents / 100, "EUR")}
											</Stats.Value>
										</Stats>
									</Stats.Container>
								),
							}}
						/>
					</Modal.TableWrapper>
					<IncomingInvoicePaymentRunForm />
				</Modal.Body>
				<Modal.FooterForm
					submitLabel={t("incoming_invoices.payments.export_payments")}
					isDisabled={hasUnpayable || !previews.length}
				/>
			</Modal>
		</FormProvider>
	);
}
